/*
 * Stargaze minigame — 60 seconds. Player anchored at the bench; shooting stars
 * cross the upper third of the sky at random intervals. Tap a star within 50px
 * of its current position to score +1 and reveal a small wish from the curated
 * pool (see content/wishes.ts).
 *
 * Parallels forage.ts and fish.ts in shape:
 *   createStargazeState() → fresh state
 *   startStargaze(state)  → arm it
 *   tickStargaze(state, dt, events) → drive the timer + spawn cadence
 *   tryHitStar(state, x, y, activeStars) → score-on-tap helper
 *
 * The renderer (render/stargaze.ts) owns the visual particle list. This module
 * owns timing, score, and "when should the next star spawn". The two
 * cooperate via the `onSpawn`/`onHit` events.
 *
 * Persistence: a small localStorage wrapper for `bestStargaze` so this module
 * ships working out-of-the-box. The parent will replace this with proper
 * SaveData wiring (per task description), but persistence is real today.
 *
 * Spawn-rate hook: tickStargaze accepts an optional `spawnIntervalScale`
 * multiplier on the events object. TASK-10 (day/night) can pass < 1 during
 * deep-twilight to make stars more frequent. Defaults to 1 (= ~1.5s avg).
 */

import type { ShootingStar } from '@/render/stargaze';
import { pickWish } from '@/content/wishes';

export const STARGAZE_DURATION_SEC = 60;

/** How close a tap must be to a star (px) to count as a hit. */
export const STARGAZE_HIT_RADIUS = 50;

/** Default mean interval between star spawns (seconds). */
export const STARGAZE_AVG_SPAWN_SEC = 1.5;

/** localStorage key the working persistence wrapper writes to. */
const STARGAZE_BEST_KEY = 'fernhollow-stargaze-best';

/** Per-run state for one stargaze sitting. */
export interface StargazeState {
  /** True while a run is in progress. */
  active: boolean;
  /** Seconds remaining in the run. */
  timeLeft: number;
  /** Stars hit this run. */
  score: number;
  /** Seconds until the next spawn fires. */
  nextSpawnIn: number;
  /** Best score persisted across sessions (loaded lazily from localStorage). */
  best: number;
}

/** Events the host wires when ticking the minigame. */
export interface StargazeTickEvents {
  /** A new shooting star should be spawned into the renderer. */
  onSpawn: (star: ShootingStar) => void;
  /** A tap connected with a star — emit the float-text wish and despawn. */
  onHit: (star: ShootingStar, wish: string) => void;
  /** The 60s timer ran out. */
  onEnd: (score: number) => void;
  /**
   * Spawn the next star via the renderer. Returned star is what onSpawn will
   * be called with. Decoupled so tests can stub a deterministic spawn.
   */
  spawnStar: () => ShootingStar;
  /**
   * Optional spawn-rate multiplier (defaults to 1). Lower than 1 = stars more
   * frequent. TASK-10 (day/night) is expected to wire this from the world
   * clock — e.g. 0.65 during deep twilight.
   */
  spawnIntervalScale?: number;
}

/** Read the persisted best score (or 0 if none yet / storage unavailable). */
function loadBest(): number {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(STARGAZE_BEST_KEY) : null;
    if (raw === null) return 0;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  } catch {
    return 0;
  }
}

/** Persist the best score. Best-effort — silently no-ops if storage is unavailable. */
function saveBest(value: number): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STARGAZE_BEST_KEY, String(value));
    }
  } catch {
    // ignore — quota or private-mode
  }
}

export function createStargazeState(): StargazeState {
  return {
    active: false,
    timeLeft: 0,
    score: 0,
    nextSpawnIn: 0,
    best: loadBest(),
  };
}

export function startStargaze(state: StargazeState): void {
  state.active = true;
  state.timeLeft = STARGAZE_DURATION_SEC;
  state.score = 0;
  // First star arrives quickly so the run feels alive — between 0.3 and 0.9s.
  state.nextSpawnIn = 0.3 + Math.random() * 0.6;
}

/**
 * Draw an exponentially-distributed interval around the mean spawn rate.
 * This gives a Poisson-process feel: most spawns near the mean, occasional
 * long gaps and short bursts. Clamped to keep things readable.
 */
function nextSpawnInterval(scale: number): number {
  const mean = STARGAZE_AVG_SPAWN_SEC * scale;
  // -ln(1-u) gives exponential with mean=1; scale by `mean`.
  const u = Math.random();
  const raw = -Math.log(1 - Math.min(0.999, Math.max(1e-6, u))) * mean;
  return Math.max(0.35, Math.min(4.5, raw));
}

/**
 * Drive the minigame for `dt` seconds. Spawns stars on its cadence and ends
 * the run when the timer hits 0. Hit-testing is handled separately in
 * `tryHitStar` — call it from the host's tap handler.
 */
export function tickStargaze(
  state: StargazeState,
  dt: number,
  events: StargazeTickEvents
): void {
  if (!state.active) return;

  state.timeLeft -= dt;
  state.nextSpawnIn -= dt;

  const scale = events.spawnIntervalScale ?? 1;

  // Spawn cadence — may fire more than once per tick if dt is huge (it won't be).
  while (state.nextSpawnIn <= 0 && state.timeLeft > 0) {
    const star = events.spawnStar();
    events.onSpawn(star);
    state.nextSpawnIn += nextSpawnInterval(scale);
  }

  if (state.timeLeft <= 0) {
    state.active = false;
    state.timeLeft = 0;
    if (state.score > state.best) {
      state.best = state.score;
      saveBest(state.best);
    }
    events.onEnd(state.score);
  }
}

/**
 * Test a tap against the currently-active stars. Returns the hit star (the
 * nearest within the hit radius) or null. The caller is responsible for
 * applying the score + invoking the `onHit` event with `pickWish()`.
 *
 * We score it here too so callers can stay thin: increments state.score and
 * marks the star as `hit` so the renderer fades it out fast.
 */
export function tryHitStar(
  state: StargazeState,
  tapX: number,
  tapY: number,
  activeStars: ShootingStar[]
): ShootingStar | null {
  if (!state.active) return null;

  let nearest: ShootingStar | null = null;
  let nearestDist = STARGAZE_HIT_RADIUS;
  for (const s of activeStars) {
    if (s.hit) continue;
    const d = Math.hypot(s.x - tapX, s.y - tapY);
    if (d <= nearestDist) {
      nearest = s;
      nearestDist = d;
    }
  }

  if (nearest === null) return null;

  nearest.hit = true;
  state.score++;
  return nearest;
}

/** Convenience: returns a wish for the just-hit star. Re-exported for symmetry. */
export function wishForHit(): string {
  return pickWish();
}
